import { Link } from "wouter";
import { ArrowRight, AlertTriangle, Lightbulb, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface WhatsNextCardProps {
  title: string;
  description: string;
  actionLabel: string;
  actionHref: string;
  icon?: React.ReactNode;
  status?: "pending" | "blocked" | "ready";
  blocker?: string;
  className?: string;
  roleTag?: string;
}

export function WhatsNextCard({
  title,
  description,
  actionLabel,
  actionHref,
  icon,
  status = "ready",
  blocker,
  className,
  roleTag,
}: WhatsNextCardProps) {
  const statusConfig = {
    pending: {
      borderColor: "border-l-muted-foreground",
      badgeVariant: "secondary" as const,
      badgeText: "Pending",
      badgeIcon: Clock,
    },
    blocked: {
      borderColor: "border-l-destructive",
      badgeVariant: "destructive" as const,
      badgeText: "Blocked",
      badgeIcon: AlertTriangle,
    },
    ready: {
      borderColor: "border-l-primary",
      badgeVariant: "default" as const,
      badgeText: "Ready",
      badgeIcon: Lightbulb,
    },
  };

  const config = statusConfig[status];
  const StatusIcon = config.badgeIcon;

  return (
    <div
      className={cn(
        "bg-card border border-card-border rounded-none border-l-4 p-4 shadow-sm",
        config.borderColor,
        className
      )}
      data-testid="card-whats-next"
    >
      <div className="flex items-start gap-3">
        {icon && (
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
            {icon}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h4 className="font-semibold text-foreground" data-testid="text-whats-next-title">
              {title}
            </h4>
            <Badge variant={config.badgeVariant} className="no-default-active-elevate" data-testid="badge-whats-next-status">
              <StatusIcon className="h-3 w-3 mr-1" />
              {config.badgeText}
            </Badge>
            {roleTag && (
              <Badge variant="outline" className="no-default-active-elevate text-xs" data-testid="badge-whats-next-role">
                {roleTag}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground" data-testid="text-whats-next-description">
            {description}
          </p>

          {status === "blocked" && blocker && (
            <div className="mt-3 p-2 bg-destructive/10 rounded-md flex items-start gap-2" data-testid="whats-next-blocker">
              <AlertTriangle className="h-4 w-4 text-destructive flex-shrink-0 mt-0.5" />
              <span className="text-sm text-destructive" data-testid="text-whats-next-blocker">
                {blocker}
              </span>
            </div>
          )}

          <div className="mt-4">
            <Button asChild size="sm" disabled={status === "blocked"} data-testid="button-whats-next-action">
              <Link href={actionHref}>
                {actionLabel}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
